import type { ReactElement } from 'react';
import type { DraftEntryDto, PlannerMealSlot } from '../../api/plannerTypes.js';

export interface MealSlotCardProps {
  slot: PlannerMealSlot;
  entry: DraftEntryDto;
  onSwap: (entry: DraftEntryDto) => void;
  onDelete: (entry: DraftEntryDto) => void;
}

const SLOT_LABEL: Record<PlannerMealSlot, string> = {
  breakfast: '🌅 Breakfast',
  lunch: '☀️ Lunch',
  dinner: '🌙 Dinner',
};

export function MealSlotCard({ slot, entry, onSwap, onDelete }: MealSlotCardProps): ReactElement {
  const titleStyle: React.CSSProperties = entry.is_leftover
    ? { textDecoration: 'line-through', opacity: 0.6 }
    : {};
  return (
    <div
      className="flex flex-col gap-2 rounded-xl border p-3"
      style={{ borderColor: 'var(--border)', background: 'var(--bg-surface)' }}
      data-testid={`planner-meal-${slot}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          {SLOT_LABEL[slot]}
          {entry.is_leftover ? (
            <span
              className="ml-2 rounded px-1 text-[10px]"
              style={{ background: 'var(--accent)', color: 'var(--bg-surface)' }}
              data-testid="planner-leftover-badge"
            >
              leftover
            </span>
          ) : null}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            data-testid={`planner-swap-${slot}`}
            onClick={() => onSwap(entry)}
            disabled={entry.is_leftover}
            className="rounded border px-2 py-1 text-xs disabled:opacity-40"
            style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
          >
            Swap
          </button>
          <button
            type="button"
            data-testid={`planner-delete-${slot}`}
            onClick={() => onDelete(entry)}
            className="rounded border px-2 py-1 text-xs"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
          >
            Delete
          </button>
        </div>
      </div>
      <div className="flex flex-col">
        <span className="text-base font-semibold" style={{ color: 'var(--text-main)', ...titleStyle }}>
          {entry.recipe.title}
        </span>
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {Math.round(entry.total_kcal)} kcal · P {entry.total_protein.toFixed(1)}g · C{' '}
          {entry.total_carbs.toFixed(1)}g · F {entry.total_fat.toFixed(1)}g · {entry.portions}×
        </span>
      </div>
    </div>
  );
}